import { useEffect, useMemo, useRef, useState } from "react";
import type {
  LabeledFile,
  UploadProps,
  UploadRowViewModel,
  UploadThumbnailType,
  UploadViewModel,
} from "@model/UIComponents.model";
import { MimeType } from "@model/Products.model";


const bytesToText = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : bytes >= 1024
      ? `${Math.round(bytes / 1024)} KB`
      : `${bytes} B`;

const fileExtension = (file: File): string =>
  file.type?.split("/")[1]?.toUpperCase() ||
  file.name.split(".").pop()?.toUpperCase() ||
  "FILE";

const isPdf = (type?: string) => type === "application/pdf";
const isImage = (type?: string) => !!type && type.startsWith("image/");

const toFileList = (files: File[] | null): FileList | null => {
  if (!files || files.length === 0) return null;
  const dataTransfer = new DataTransfer();
  files.forEach((file) => dataTransfer.items.add(file));
  return dataTransfer.files;
};

const isLabeledFileArray = (value: unknown): value is LabeledFile[] =>
  Array.isArray(value) && value.every((v) => v && typeof v === "object" && "file" in v && "fileLabel" in v);

export function useUpload({
  value,
  onChange,
  mimeTypes,
  required = false,
  maxSizeMB = 5,
  maxFiles = 1,
  maxTotalMB,
  badTypeMessage,
  existingFiles = [],
  onRemoveExisting,
  showInlineError = true,
  error,
  helperText,
  requiresLabel = false,
}: UploadProps): UploadViewModel {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isAllowedType = (type: string): boolean =>
    !mimeTypes || mimeTypes.includes(type as MimeType);

  const allowSingleFile = maxFiles <= 1;
  const accept = useMemo(() => mimeTypes?.join(",") ?? "", [mimeTypes]);

  // derive raw File[] regardless of whether value is FileList or LabeledFile[]
  const selectedFiles = useMemo<File[]>(() => {
    if (!value) return [];
    if (isLabeledFileArray(value)) return value.map((v) => v.file);
    if (value instanceof FileList) return Array.from(value);
    return [];
  }, [value]);

  // seed labels from value if it's already LabeledFile[]
  const [labels, setLabels] = useState<Record<number, string>>(() => {
    if (isLabeledFileArray(value)) {
      const init: Record<number, string> = {};
      value.forEach((v, i) => {
        init[existingFiles.length + i] = v.fileLabel ?? "";
      });
      return init;
    }
    return {};
  });

  const [touched, setTouched] = useState<Record<number, boolean>>({});
  const [previewUrls, setPreviewUrls] = useState<Record<number, string>>({});

  useEffect(() => {
    Object.values(previewUrls).forEach(URL.revokeObjectURL);

    const nextUrls: Record<number, string> = {};
    selectedFiles.forEach((file, index) => {
      if (isImage(file.type)) nextUrls[index] = URL.createObjectURL(file);
    });

    setPreviewUrls(nextUrls);

    return () => Object.values(nextUrls).forEach(URL.revokeObjectURL);
  }, [selectedFiles]);

  // when requiresLabel: fire onChange with LabeledFile[] and sync RHF error state
  useEffect(() => {
    if (!requiresLabel) return;

    const labeled: LabeledFile[] = selectedFiles.map((file, i) => ({
      file,
      fileLabel: labels[existingFiles.length + i] ?? "",
    }));

    onChange(labeled.length > 0 ? labeled : null);
  }, [labels, selectedFiles.length, requiresLabel]);

  // internal helper — always works with File[]
  const setFiles = (files: File[] | null) => {
    onChange(toFileList(files));

    if (!files?.length && inputRef.current) inputRef.current.value = "";
  };

  const openFilePicker = () => {
    if (!allowSingleFile && selectedFiles.length >= maxFiles) return;
    if (inputRef.current) inputRef.current.value = "";
    inputRef.current?.click();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const pickedFiles = event.target.files ? Array.from(event.target.files) : [];

    if (allowSingleFile) {
      setFiles(pickedFiles.slice(0, 1));
      return;
    }

    const remainingSlots = Math.max(
      0,
      maxFiles - existingFiles.length - selectedFiles.length
    );
    setFiles([...selectedFiles, ...pickedFiles.slice(0, remainingSlots)]);
  };

  const removeAllNewFiles = () => {
    setLabels({});
    setFiles(null);
  };

  const removeNewFileAt = (index: number) => {
    // re-key labels to stay aligned after removal
    setLabels((prev) => {
      const removedRowIdx = existingFiles.length + index;
      const next: Record<number, string> = {};
      Object.entries(prev).forEach(([k, v]) => {
        const ki = Number(k);
        if (ki < removedRowIdx) next[ki] = v;
        else if (ki > removedRowIdx) next[ki - 1] = v; // shift down
      });
      return next;
    });
    const next = selectedFiles.filter((_, i) => i !== index);
    setFiles(next.length ? next : null);
  };

  const handleLabelChange = (rowIndex: number, val: string) => {
    setLabels((prev) => ({ ...prev, [rowIndex]: val }));
  };

  const handleLabelBlur = (rowIndex: number) => {
    setTouched((prev) => ({ ...prev, [rowIndex]: true }));
  };

  const totalCount = existingFiles.length + selectedFiles.length;
  const hasFiles = totalCount > 0;

  const inlineError = (() => {
    if (!hasFiles) return required ? "File is required" : null;
    if (totalCount > maxFiles)
      return `Maximum ${maxFiles} file${maxFiles > 1 ? "s" : ""} allowed`;
    if (typeof maxTotalMB === "number") {
      const totalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);
      if (totalSize > maxTotalMB * 1024 * 1024)
        return `Total size must not exceed ${maxTotalMB} MB`;
      }
    for (const file of selectedFiles) {
      if (!isAllowedType(file.type))
        return badTypeMessage ?? "File type is not allowed";
      if (file.size > maxSizeMB * 1024 * 1024)
        return `Each file must be ≤ ${maxSizeMB} MB`;
      }
    return null;
  })();

  const errorMessage = error || (showInlineError ? inlineError : null);
  const primaryButtonLabel = !hasFiles ? "Upload" : allowSingleFile ? "Replace" : "Add files";
  const primaryButtonDisabled = !allowSingleFile && totalCount >= maxFiles;
  const remainingMessage = !allowSingleFile
      ? maxFiles - totalCount > 0
      ? `You can add ${maxFiles - totalCount} more file${maxFiles - totalCount > 1 ? "s" : ""}.`
        : `You've reached the maximum of ${maxFiles}.`
      : null;

  let rowIndex = 0;

  const rows: UploadRowViewModel[] = [
    ...(allowSingleFile && selectedFiles.length > 0
      ? []
      : existingFiles.map((file, index): UploadRowViewModel => {
          const currentIndex = rowIndex++;
        const thumbnailType: UploadThumbnailType = isPdf(file.type)
            ? "pdf" : isImage(file.type) ? "image" : "fallback";
          const labelValue = labels[currentIndex] ?? "";
          const labelError = requiresLabel && (touched[currentIndex] ?? false) && !labelValue.trim()
            ? "Label is required" : "";
        return {
          key: `existing-${file.name}-${index}`,
          kind: "existing",
          fileName: file.name,
          metaText: "Saved",
          thumbnailType,
          url: file.url ?? null,
          thumbnailUrl: thumbnailType === "image" ? file.url ?? null : null,
          canRemove: !!onRemoveExisting,
          onRemove: onRemoveExisting ? () => onRemoveExisting(index) : undefined,
          testId: `fileupload-existing-${index}`,
            labelValue,
            labelError,
            onLabelChange: requiresLabel ? (val) => handleLabelChange(currentIndex, val) : undefined,
            onLabelBlur: requiresLabel ? () => handleLabelBlur(currentIndex) : undefined,
        };
      })),

    ...selectedFiles.map((file, index): UploadRowViewModel => {
      const currentIndex = rowIndex++;
      const thumbnailType: UploadThumbnailType = isPdf(file.type)
        ? "pdf" : isImage(file.type) ? "image" : "fallback";
      const labelValue = labels[currentIndex] ?? "";
      const labelError = requiresLabel && (touched[currentIndex] ?? false) && !labelValue.trim()
        ? "Label is required" : "";
      return {
        key: `new-${file.name}-${file.lastModified}-${index}`,
        kind: "new",
        fileName: file.name,
        metaText: `${bytesToText(file.size)} • ${fileExtension(file)}`,
        thumbnailType,
        thumbnailUrl: thumbnailType === "image" ? previewUrls[index] ?? null : null,
        canRemove: true,
        onRemove: () => allowSingleFile ? removeAllNewFiles() : removeNewFileAt(index),
        testId: `fileupload-item-${index}`,
        labelValue,
        labelError,
        onLabelChange: requiresLabel ? (val) => handleLabelChange(currentIndex, val) : undefined,
        onLabelBlur: requiresLabel ? () => handleLabelBlur(currentIndex) : undefined,
      };
    }),
  ];


  return {
    inputRef,
    accept,
    allowMultiple: !allowSingleFile,

    hasFiles,
    rows,

    primaryButtonLabel,
    primaryButtonDisabled,
    primaryButtonTitle: primaryButtonDisabled ? "Maximum reached" : undefined,

    remainingMessage,
    helperMessage: helperText,
    errorMessage,

    openFilePicker,
    handleInputChange,
  };
}
